import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ContentChildren,
  EventEmitter,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  Renderer2,
  ElementRef,
  ComponentFactoryResolver,
  ViewContainerRef,
} from '@angular/core';
import { PrimeTemplate } from 'primeng/api';
import { Checkbox } from 'primeng/checkbox';
import { Table, TableService } from 'primeng/table';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { DataTablePreference } from 'src/app/core/models/user-preference';
import { deepCopy, isNullOrEmpty, isNumeric, onChanges } from 'src/app/core/utils/common-functions';
import { SessionBaseComponent } from '../session.base.component';
import { SessionStorageService } from './../../../core/services/session-storage.service';
import { DataTableSavedBean } from './bean/data-table-saved-bean';
import { CommonDataService } from 'src/app/core/services/common-data.service';
import { CustomRowsPerPageComponent } from '../custom-rows-per-page/custom-rows-per-page.component';

@Component({
  selector: 'laas-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  providers: [
    Table,
    TableService
  ]
})
export class DataTableComponent extends SessionBaseComponent implements OnChanges, OnInit, AfterContentInit, AfterViewInit {

  @Input() styleClass = 'p-datatable-sm p-datatable-striped p-datatable-gridlines';
  @Input() style?: any;
  @Input() tableKey?: string; //  需要自定義欄位功能必須設 tableKey
  @Input() tableTitle: any;
  @Input() tableWidth?: string;
  @Input() defaultColumns: any[] = [];
  @Input() dataKey: any = 'rowId';
  @Input() value: any[] = [];
  @Input() backupValue: any[] = [];
  @Input() totalRecords: any = 0;
  //  Column Reorder
  @Input() reorderableColumns: boolean = true;
  //  Column Resize
  @Input() resizableColumns: boolean = true;
  @Input() columnResizeMode: string = 'expand';
  //  Column Toggle
  @Input() toggleable: boolean = true;
  //  Expanded
  @Input() expandable: boolean = false;
  @Output() rowExpanded: EventEmitter<any> = new EventEmitter();
  //  Lazy
  @Input() lazy: boolean = false;
  @Input() loading: boolean = false;
  //  Page
  @Input() currentPageReportTemplate: any = '';
  @Input() paginator: boolean = true;
  @Input() first: number = 0;
  @Input() rows: any = 5;
  @Input() rowsPerPageOptions: any[] = [5, 10, 25, 50, 100];
  @Input() showCurrentPageReport: boolean = true;
  @Output() tableOnPage: EventEmitter<any> = new EventEmitter();
  @Output() setPageSize: EventEmitter<any> = new EventEmitter();
  //  Selection
  @Input() selectable: boolean = false;
  @Input() selectionMode: 'single' | 'multiple' = 'single';
  @Input() selectedRows: any = [];
  @Input() rowHover: boolean = false;
  @Output() onRowSelect: EventEmitter<any> = new EventEmitter();
  @Output() selectedRowsChange: EventEmitter<any> = new EventEmitter();
  //  Click
  @Output() onRowClick: EventEmitter<any> = new EventEmitter();
  //  Expand
  @Input() rowExpandMode: 'multiple' | 'single' = 'single';
  @Input() expandableRows: string = 'false';
  //  Scroll
  @Input() scrollable: boolean = true;
  @Input() scrollHeight: string = '60vh';
  //  Sort
  @Input() sortMode: 'single' | 'multiple' = 'single';
  @Output() tableOnSort: EventEmitter<any> = new EventEmitter();
  //  Edit
  @Input() editPermission?: PermissionConstants;
  @Input() isShowDeleteFunction: boolean = false;
  @Input() inputValid!: (selectedRow: any) => any;
  @Input() createNewRow!: (dataKey: number) => any;
  @Input() isShowCreateRowFunction: boolean = true;
  @Input() isShowSaveFunction: boolean = true;
  @Input() isShowExportFunction: boolean = false;
  @Input() isShowImportFunction: boolean = false;
  @Output() createRow: EventEmitter<any> = new EventEmitter();
  @Output() savedData: EventEmitter<any> = new EventEmitter();
  @Output() restoreData: EventEmitter<any> = new EventEmitter();
  @Output() exportData: EventEmitter<any> = new EventEmitter();
  @Output() importData: EventEmitter<any> = new EventEmitter();

  @Input() rowStyle!: (rowData: any) => any;

  @ViewChild('dt') table!: Table;
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('headerCheckBox') headerCheckBox!: Checkbox;
  @ViewChildren('rowCheckbox') rowCheckbox!: QueryList<Checkbox>;
  customRowsPerPageComponent: CustomRowsPerPageComponent | null = null;

  @ContentChildren(PrimeTemplate) templates!: QueryList<PrimeTemplate>;
  captionTemplate!: TemplateRef<any>;
  outerCaptionTemplate!: TemplateRef<any>;
  headerTemplate!: TemplateRef<any>;
  headerColumnFilterTemplate!: TemplateRef<any>;
  bodyTemplate!: TemplateRef<any>;
  bodyInputTemplate!: TemplateRef<any>;
  bodyOutputTemplate!: TemplateRef<any>;
  expandedRowTemplate!: TemplateRef<any>;
  footerTemplate!: TemplateRef<any>;

  selectedCols: any[] = [];
  headerDefaultColCount: any = 0;
  // 固定在 header 前幾行的 width
  preHeaderDefaultWidths: string[] = [];

  expandedRows: any = {};

  selectedRow: any;
  checkedRows: any[] = [];
  previousSelectedRow: any;
  isEdit: boolean = false;
  editMode: 'cell' | 'row' = 'row';
  isInEditMode: boolean = false;
  savedDataBean: DataTableSavedBean = new DataTableSavedBean();
  newDataKey: number = 1;
  dateList: string[] = [];
  private skipBackupOnNextInit: boolean = false;

  columnDefaultWidth!: number;

  constructor(
    injector: Injector,
    private sessionStoreageService: SessionStorageService,
    private commonDataService: CommonDataService,
    private zone: NgZone,
    private el: ElementRef,
    private resolver: ComponentFactoryResolver,
    private viewContainerRef: ViewContainerRef) {
    super(injector);

    //  set Language dropdown event
    this.translateService.onLangChange.subscribe((e: Event) => {
      this.pageSummaryTranslation();
    });

    //  get PageSummary by language
    this.pageSummaryTranslation();
  }

  get dataTablePreference(): DataTablePreference | null {
    return this.userService.preference.dataTablePreferences.find(x => x.tableName === this.tableKey) ?? null;
  }

  @Input() get selectedColumns(): any[] {
    return this.selectedCols;
  }

  set selectedColumns(cols: any[]) {
    this.defaultColumns.forEach((x) => {
      const idx = cols.findIndex(y => y.field === x.field);
      x.display = idx > -1;
    });
    this.selectedCols = this.defaultColumns.filter((x: any) => x.display);
  }

  pageSummaryTranslation(): void {
    this.translateService.get('Common.Table.PageSummary')
      .subscribe({
        next: result => {
          this.currentPageReportTemplate = result;
        }
      });
  }

  async ngOnInit(): Promise<void> {
    let calcWidth = window.innerWidth - 250;
    if (this.tableWidth) {
      const rootFontSize = getComputedStyle(document.documentElement).fontSize;
      calcWidth = parseFloat(this.tableWidth) * parseFloat(rootFontSize);
    }
    const screenWidth = calcWidth;
    const totalColumnDisplay = this.defaultColumns.filter((x: any) => x.display).length;
    this.columnDefaultWidth = (screenWidth / totalColumnDisplay) < 100 ? 100 : (screenWidth / totalColumnDisplay);
    if (!Number.isFinite(this.columnDefaultWidth)) {
      this.columnDefaultWidth = 100;
    }

    if (this.selectable) {
      this.headerDefaultColCount += 1;
      this.preHeaderDefaultWidths.push('50');
      this.selectionMode = 'multiple';
    }
    if (this.expandable) {
      this.headerDefaultColCount += 1;
      this.preHeaderDefaultWidths.push('50');
    }
    this.defaultColumns.forEach((col, index) => {
      if (col.order === undefined) {
        col.order = index;
      }
      if (col.field === '__delete') {
        col.width = col.width ?? 70;
      } else {
        col.width = col.width && isNumeric(col.width) ? col.width : this.columnDefaultWidth;
      }
    });
    this.selectedCols = this.defaultColumns;

    if (this.editPermission) {
      this.userService.getUserProfile().toPromise().then(result => {
        if (result && result.userProfile && result.userProfile.roles) {
          const permissionCodes = (Object.values(result.userProfile.roles)
            .reduce((c: any, i: any) => c.concat(i.permissions), []) as any).map((x: any) => {
              return x.permissionCode;
            });

          if (permissionCodes.includes(this.editPermission)) {
            this.isEdit = true;
            this.editMode = 'row';
          }
        }
      });
    }

    this.fetchDefaultRowsPerPageOptions();
  }

  initDefaultColumns(){
    this.defaultColumns.forEach((col, index) => {
      if (col.order === undefined) {
        col.order = index;
      }
      if (col.field === '__delete') {
        col.width = col.width?? 70;
      } else {
        col.width = col.width?? this.columnDefaultWidth;
      }
    });
  }

  ngAfterContentInit(): void {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case 'caption':
          this.captionTemplate = item.template;
          break;
        case 'outerCaption':
          this.outerCaptionTemplate = item.template;
          break;
        case 'header':
          this.headerTemplate = item.template;
          break;
        case 'headerCoulmnFilter':
          this.headerColumnFilterTemplate = item.template;
          break;
        case 'body':
          this.bodyTemplate = item.template;
          break;
        case 'bodyInputTemplate':
          this.bodyInputTemplate = item.template;
          break;
        case 'bodyOutputTemplate':
          this.bodyOutputTemplate = item.template;
          break;
        case 'rowexpansion':
          this.expandedRowTemplate = item.template;
          break;
        case 'footer':
          this.footerTemplate = item.template;
          break;
        default:
          break;
      }
    });
  }

  ngAfterViewInit(): void {
    if (!isNullOrEmpty(this.tableKey)) {
      this.table.stateKey = this.tableKey as string;
    }

    this.zone.run(() => {
      setTimeout(() => {
        this.loadTablPreference();
        this.addPaginatorBottomTemplate();
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      // table value變動就初始化變數
      this.initSetupValue();
      if (this.customRowsPerPageComponent) {
        this.customRowsPerPageComponent.disabled = !this.value.length;
      }
    }
    if (changes.rows) {
      if (this.customRowsPerPageComponent) {
        this.customRowsPerPageComponent.rowsPerPage = this.rows;
      }
    }
    onChanges(changes, this.selectedCols);
  }

  /**
   * 由 this.table.columns 存在的col取得 width string
   */
  getWidths(): string {
    let widthArr = this.table.columns?.map(col => col.width) ?? [];
    return this.preHeaderDefaultWidths.concat(widthArr).join(",");
  }

  /**
   * 根據 this.defaultColumns 的 order 去排序 this.table.columns
   */
  syncTableColumnsOrder() {
    const fieldOrderMap = new Map<string, number>(this.defaultColumns.map(col => ([col.field, col.order])));
    this.table.columns?.sort((a,b) => {
      let aOrder = fieldOrderMap.get(a.field);
      let bOrder = fieldOrderMap.get(b.field);
      if(isNullOrEmpty(aOrder) && isNullOrEmpty(bOrder)) {
        return 0;
      }
      return ( (aOrder ?? Infinity) - (bOrder ?? Infinity))
    })
  }
  /**
   * 根據 this.defaultColumns 的 display 去顯示 this.table.columns
   */
  syncTableColumnsDisplay() {
    this.table.columns = this.table.columns?.filter(col => this.defaultColumns.find(x => x.field === col.field).display);
  }

  /**
   * 根據畫面變動的 this.table.columns 情況去更新 this.defaultColumns 的 order
   * 若顯示的 order 為 3,2,7,1 根據這幾個 field 改為 1,2,3,7, 這樣不影響後續4,5,6,8再插入的位置
   */
  syncDefaultColumnsOrder() {
    const existFieldMapIndex = new Map<string, number>(this.table.columns?.map((col, index) => ([col.field, index])));
    const targetFields = this.defaultColumns.filter(x => existFieldMapIndex.has(x.field) );
    const oldOrder = targetFields.map(x => x.order);
    targetFields.sort((a,b) => {
      let aIndex = existFieldMapIndex.get(a.field);
      let bIndex = existFieldMapIndex.get(b.field);
      if(isNullOrEmpty(aIndex) && isNullOrEmpty(bIndex)) {
        return 0;
      }
      return ( (aIndex ?? Infinity) - (bIndex ?? Infinity))
    });
    const newOrder = [...oldOrder].sort((a,b) => a-b);
    // set 入
    targetFields.forEach((col, index) => {
      col.order = newOrder[index];
    });
    // 重新排序
    this.defaultColumns.sort((a,b) => a.order - b.order);
  }
  /**
   * 同步更新 this.defaultColumns
   * 若存在於 this.table.columns 則 display = true
   */
  syncDefaultColumnsDisplay() {
    const existsCol =  this.table.columns?.map(col => col.field) ?? [];
    this.defaultColumns.forEach(col => {
      col.display = existsCol.includes(col.field);
    });
  }

  colResized(event: any): void {
    this.syncDefaultColumnsDisplay();
    // colResized 不會更新 this.table.columns, 需要自己手動更新
    const field = event.element.getAttribute('field');
    const targetCol = this.defaultColumns.find(x => x.field === field);
    if (targetCol) {
      targetCol.width = event.element.offsetWidth;
    }

    // restoreColumnWidths 完全依靠 columnWidthsState 與 tableWidthState
    // 但 columnWidthsState 僅有在 stateKey 存在才會更新, 所以這邊只能手動找現存的 width 去更新
    this.table.columnWidthsState = this.getWidths();
    this.table.tableWidthState = this.table.columnWidthsState.split(',').reduce((sum: number, width: string) => sum += parseInt(width, 10), 0) + '';
  }

  colReordered(event: any): void {
    // 同步更換順序的 this.table.columns 到 this.defaultColumns
    this.syncDefaultColumnsOrder();
    this.syncDefaultColumnsDisplay();

    // reorder 在 onColumnDrop 不會根據已換順序的 this.table.column 去更新寬度和 columnWidthsState, 需要手動更新再 restoreColumnWidths
    this.table.columnWidthsState = this.getWidths();
    this.table.tableWidthState = this.table.columnWidthsState.split(',').reduce((sum: number, width: string) => sum += parseInt(width, 10), 0) + '';
    // onColumnDrop 在 emit onColReorder 後才去 saveState. restoreColumnWidths 需在其後執行
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.table.restoreColumnWidths();
      }, 0);
    });

  }
  colToggled() {
    // 若是 toggle 勾選顯示欄位, toggle 會將消失或復原的項目, 反應在 this.table.columns, 所以這邊也反應到 defaultColumns 的 display
    this.syncDefaultColumnsDisplay();
    if(isNullOrEmpty(this.tableKey) || !this.authenticationService.isAuthenticated) {
      // 沒有 tableKey 情形, 不會自動去更新寬度, 針對這流程補這段
      this.initDefaultColumns();
      this.table.columnWidthsState = this.getWidths();
      this.table.tableWidthState = this.table.columnWidthsState.split(',').reduce((sum: number, width: string) => sum += parseInt(width, 10), 0) + '';
      this.table.restoreColumnWidths();
    }
    // 去觸發後續 saveTablePreference
    this.table.saveState();
  }

  rowExpandedEvent(event: any, rowData: any, expanded: any): void {
    if (rowData[this.dataKey]) {
      const expandKey = rowData[this.dataKey].toString();
      this.expandedRows[expandKey] = 0;
      if (expanded) {
        this.expandedRows[expandKey] = 1;
      }
    }

    this.updateTableHighlight(rowData);
    this.rowExpanded.emit({ originEvent: event, rowData, expanded });
  }

  collapseAllRow(): void {
    this.expandedRows = {};
  }

  pageEvent(event: any): void {
    // if (this.headerCheckBox) {
    //   this.headerCheckBox.writeValue(false);
    //   this.toggleAllRow();
    // }
    this.tableOnPage.emit(event);
  }

  setCurrentPage(pageIndex: number): void {
    this.first = pageIndex * this.rows;
  }

  onRowSelectEvent(event: any): void {
    this.onRowSelect.emit(event.data);
  }

  selectionChange(event: any): void {
    this.selectedRows = event;
    this.selectedRowsChange.emit(this.selectedRows);
  }

  sortEvent(event: any): void {
    this.tableOnSort.emit(event);
  }


  /**
   * 觸發時機:
   * 1. 將 reorder 與 resize toggle 的資訊存到 userPreference
   */
  saveTablePreference(event: any): void {
    this.syncDefaultColumnsDisplay();
    // console.log("save state?: ", event);
    // console.log("tableWidth?: ", event.tableWidth);
    if (isNullOrEmpty(this.tableKey) || !this.authenticationService.isAuthenticated) {
      return;
    }

    // 儲存local storage的user preference
    const state: any = {};
    state.sort = { sortField: event.sortField, sortOrder: event.sortOrder };
    state.columns = this.defaultColumns;
    state.rows = event.rows;
    state.columnWidths = this.getWidths();
    state.rowsPerPageOptions = this.rowsPerPageOptions;

    const userPreference = this.userService.preference;
    let dataTablePreference = userPreference?.dataTablePreferences?.find(x => x.tableName === this.tableKey);
    if (dataTablePreference) {
      dataTablePreference.state = state;
    } else {
      dataTablePreference = { tableName: this.tableKey as string, state };
      userPreference.dataTablePreferences.push(dataTablePreference);
    }
    this.userService.preference = userPreference;

    // 載入user preference
    this.loadTablPreference();
  }

  /**
   * load 僅從 preference 取得資訊, 存到 sessionStorage, 再載入到 this.table.columns 與 defaultColumns 的所有情境
   */
  private loadTablPreference(): void {
    // 有些 defaultColumns 比較晚 load 進來, 若有屬性為空再初始化
    this.initDefaultColumns();
    // console.log("this.tableKey: ", this.tableKey);
    // console.log("this.table.columns: ", {...this.table.columns});
    // 新畫面沒有自定義，使用預設寬度
    if (isNullOrEmpty(this.tableKey) || isNullOrEmpty(this.dataTablePreference)) {
      // console.log("first this.tableKey: ", this.tableKey);
      // console.log("this.dataTablePreference: ", this.dataTablePreference);

      this.table.columnWidthsState = this.getWidths();
      this.table.tableWidthState = this.table.columnWidthsState.split(',').reduce((sum: number, width: string) => sum += parseInt(width, 10), 0) + '';

      this.table.restoreColumnWidths();
      return;
    }
    // 在 onStateSave 後, sessionStorage 屬性會被 p-table 更新
    // console.log("loadPreference this.tableKey: ", this.tableKey);

    // 以下拿session storage的preference 去載入
    const srcState = this.sessionStoreageService.getItem(this.tableKey as string) ?? { first: 0, rows: 5, selection: [] };

    if (srcState.selection) {
      srcState.selection = [];
      srcState.expandedRowKeys = {};
    }

    const preference = this.dataTablePreference as DataTablePreference;
    if (isNullOrEmpty(preference.state?.columns) || preference.state.columns.length === 0) {
      return;
    }
    // columns 載入到 this.table.columns
    let stateColumns: any[] = preference.state.columns;
    stateColumns = stateColumns.sort((a, b) => a.order > b.order ? 1 : -1);

    stateColumns.forEach((stateCol, index) => {

      const targetCol = this.defaultColumns.find(x => x.field === stateCol.field);
      if (targetCol) {
        targetCol.order = stateCol.order;
        targetCol.display = stateCol.display;
        targetCol.width = stateCol.width ?? this.columnDefaultWidth;
      }
    });
    this.defaultColumns.sort((a,b) => a.order - b.order);
    this.syncTableColumnsDisplay();
    this.syncTableColumnsOrder();

    this.rows = preference.state?.rows;
    this.setPageSize.emit({
      rows: this.rows
    });
    const rppo = preference.state?.rowsPerPageOptions;
    if (rppo && rppo.length > 0) {
      this.rowsPerPageOptions = rppo;
    }

    // 在同一個 sessionStorage, 且 this.table.isStateful 的情況, 他會去 sessionStorage 取值來記錄排序情況
    if (isNullOrEmpty(preference.state.sort)) {
      srcState.sortField = preference.state.sort.sortField;
      srcState.sortOrder = preference.state.sort.sortOrder;
    }


    srcState.columnWidths = preference.state.columnWidths;
    const widths = this.getWidths();
    srcState.tableWidth = widths.split(',').reduce((sum: number, width: string) => sum += parseInt(width, 10), 0);

    // 儲存session storage的preference
    this.sessionStoreageService.setItem(this.tableKey as string, srcState);


    // 刷新table欄位寬度
    this.table.columnWidthsState = widths;
    this.table.tableWidthState = srcState.tableWidth;
    this.table.restoreColumnWidths();

  }

  updateTableData(tableData: any[], skipBackup: boolean = false): void {

    this.savedDataBean = {
      newList: [],
      updatedList: [],
      deletedList: []
    };
    if (!tableData) {
      this.value = [];
      this.backupValue = [];
      this.selectedRow = null;
      this.previousSelectedRow = this.selectedRow;
      return;
    }
    this.previousSelectedRow = null;
    let index = 0;
    tableData.forEach(x => x[this.dataKey] = !x[this.dataKey] ? (x.seqNo ? x.seqNo : index++) : x[this.dataKey]);
    this.newDataKey = Math.max(...tableData.map(o => o[this.dataKey])) + 1;
    
    // 設置標記以控制 ngOnChanges 中的 initSetupValue 行為
    this.skipBackupOnNextInit = skipBackup;
    
    this.value = deepCopy(tableData) ?? [];
    
    // 只有在非skipBackup模式下才同步backupValue
    if (!skipBackup) {
      this.backupValue = deepCopy(tableData) ?? [];
    }

    if (this.value && this.value.length > 0) {
      this.selectedRow = this.value[0];
      this.previousSelectedRow = this.selectedRow;
      this.updateTableHighlight(this.selectedRow);
    }
  }

  setPageInfo(totalRecords: any): void {
    this.totalRecords = totalRecords;
  }

  toggleAllRow(): void {
    this.rowCheckbox.toArray().forEach(checkbox => {
      checkbox.writeValue(this.headerCheckBox.checked);
    });

    this.checkedRows = this.headerCheckBox.value ? (deepCopy(this.value) ?? []) : [];
    this.selectionChange(this.checkedRows);
  }

  toggleRowCheckbox(event: any, rowData: any): void {
    if (event.checked) {
      this.checkedRows.push(rowData);

      this.selectedRow = rowData;
      this.previousSelectedRow = this.selectedRow;
      this.updateTableHighlight(rowData);

      if (this.headerCheckBox && this.checkedRows.length === this.value.length) {
        this.headerCheckBox.writeValue(true);
      }
      this.selectionChange(this.checkedRows);
      return;
    }

    const removedIndex = this.checkedRows.findIndex(checkedRowData => checkedRowData[this.dataKey] == rowData[this.dataKey]);
    this.checkedRows.splice(removedIndex, 1);

    if (this.headerCheckBox && this.checkedRows.length !== this.value.length) {
      this.headerCheckBox.writeValue(false);
    }
    this.selectionChange(this.checkedRows);
  }

  stopBubblingPhases(event: Event) {
    event.stopPropagation()
  }

  cancelRowEdit(): void {
    if (this.isInEditMode && this.selectedRow) {
      this.table.cancelRowEdit(this.selectedRow);
    }
  }

  onRowClickEvent(rowData: any): void {
    this.selectedRow = rowData;
    if (this.isReselectRow()) {
      if (this.isInEditMode) {
        this.updateTableHighlight(this.previousSelectedRow);
      }
      this.onRowClick.emit(rowData);
      return;
    }

    if (this.isInEditMode) {
      if (this.inputValid && !this.inputValid(this.previousSelectedRow)) {
        this.selectedRow = this.previousSelectedRow;
        this.updateTableHighlight(this.previousSelectedRow);
        this.onRowClick.emit(rowData);
        return;
      }
      this.table.cancelRowEdit(this.previousSelectedRow);
      this.table.initRowEdit(this.selectedRow);
    }
    this.previousSelectedRow = this.selectedRow;
    this.updateTableHighlight(this.previousSelectedRow);
    this.onRowClick.emit(rowData);
  }

  isReselectRow(): boolean {
    return this.previousSelectedRow != null &&
      (this.previousSelectedRow[this.dataKey] && this.previousSelectedRow[this.dataKey] === this.selectedRow[this.dataKey]);
  }

  createRowEvent() {
    if (!this.inputValid(this.selectedRow)) {
      return;
    }
    const newRow = this.createNewRow(this.newDataKey++);
    this.value.splice(0, 0, newRow);

    this.clearTableEdit();
    this.table.initRowEdit(this.selectedRow);
    this.createRow.emit(newRow);
  }

  deleteRowEvent(): void {
    this.confirmationService.confirm({
      header: this.translateService.instant('Common.Confirm.Header'),
      message: this.translateService.instant('Common.Confirm.ConfirmDelete'),
      icon: 'pi pi-info-circle',
      accept: () => {
        if (this.checkedRows.length > 0) {
          this.table.cancelRowEdit(this.selectedRow);

          for (let checkedRow of this.checkedRows) {
            const selectedRowIndex = this.value.findIndex(row => row[this.dataKey] === checkedRow[this.dataKey]);
            if (selectedRowIndex > -1) {
              this.value.splice(selectedRowIndex, 1);
              if (!isNullOrEmpty(checkedRow.seqNo)) {
                this.addDeletedList(checkedRow.seqNo);
              }
            }
          }

          if (this.value.length <= 0) {
            this.headerCheckBox.writeValue(false);
            this.checkedRows = [];
            return;
          }

          this.selectedRow = this.value[0];
          this.previousSelectedRow = this.selectedRow;
          this.table.initRowEdit(this.selectedRow);
          this.updateTableHighlight(this.selectedRow);
          this.checkedRows = [];

        }
      }
    });
  }

  deleteRow(rowData: any): void {
    this.table.cancelRowEdit(rowData);
    const index = this.value.findIndex(item => item.rowId === rowData.rowId);
    this.value.splice(index, 1);
    if (this.value.length > 0) {
      this.selectedRow = this.value[0];
      this.table.initRowEdit(this.selectedRow);
      this.updateTableHighlight(this.selectedRow);
    } else {
      this.selectedRow = undefined;
    }
    this.previousSelectedRow = this.selectedRow;
  }

  addDeletedList(seqNo: number): void {
    if (!isNullOrEmpty(seqNo)) {
      this.savedDataBean.deletedList.push(seqNo);
    }
  }

  saveEvent(): void {
    this.confirmationService.confirm({
      header: this.translateService.instant('Common.Confirm.Header'),
      message: this.translateService.instant('Common.Confirm.Save'),
      icon: 'pi pi-info-circle',
      accept: () => {
        if (!this.inputValid(this.selectedRow)) {
          return;
        }

        this.loading = true;
        this.genDataTableSavedDataBean();
        this.savedData.emit(this.savedDataBean);
        this.savedDataBean = {
          newList: [],
          updatedList: [],
          deletedList: []
        };
      }
    });
  }

  genDataTableSavedDataBean(): void {
    for (const rowData of this.value) {
      if (!rowData.seqNo) {
        this.savedDataBean.newList.push(rowData);
        continue;
      }

      if (this.savedDataBean.deletedList.indexOf(rowData.seqNo!) == -1) {
        const originValue = this.backupValue.find(x => x.seqNo === rowData.seqNo);
        if (JSON.stringify(rowData) != JSON.stringify(originValue)) {
          this.savedDataBean.updatedList.push(rowData);
        }
      }
    }
  }

  saveSuccessEvent(): void {
    this.table.cancelRowEdit(this.selectedRow);
    this.isInEditMode = false;
  }

  restoreEvent(): void {
    this.confirmationService.confirm({
      header: this.translateService.instant('Common.Confirm.Header'),
      message: this.translateService.instant('Common.Confirm.Restore'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.restoreDataEvent();

        if (this.isInEditMode) {
          this.table.initRowEdit(this.selectedRow);
        }

        this.toastService.success(this.translateService.instant('Common.Toast.RestoreSuccess'));
      }
    });
  }

  restoreDataEvent(): void {
    this.value = deepCopy(this.backupValue)!;
    this.value.forEach(x => {
      this.dateList.forEach(i => {
        if (!isNullOrEmpty(x[i])) {
          x[i] = new Date(x[i]);
        }
      })
    });

    this.clearTableEdit();
    this.checkedRows = [];
    if (this.headerCheckBox) {
      this.headerCheckBox.writeValue(false);
    }
    this.savedDataBean.newList = [];
    this.savedDataBean.updatedList = [];
    this.savedDataBean.deletedList = [];
    this.table.resetScrollTop();
    this.restoreData.emit();
  }

  private clearTableEdit(): void {
    if (this.isInEditMode) {
      this.table.cancelRowEdit(this.selectedRow);
    }
    this.selectedRow = this.value[0];
    this.previousSelectedRow = this.selectedRow;
    this.updateTableHighlight(this.selectedRow);
  }

  updateTableHighlight(currentRow: any | null): void {
    // if (!currentRow) {
    //   return;
    // }
    // console.log(currentRow);
    // // this.selectedRows = currentRow;
    // this.table.selectionKeys = {};
    // this.table.selectionKeys[currentRow[this.dataKey]] = 1;
    // this.table.tableService.onSelectionChange();
  }

  switchEditModeEvent(): void {
    if (!this.isInEditMode) {
      this.isInEditMode = true;
      if (!isNullOrEmpty(this.selectedRow)) {
        this.table.initRowEdit(this.selectedRow);
      }
      return;
    }

    this.confirmationService.confirm({
      header: this.translateService.instant('Common.Confirm.Header'),
      message: this.isShowSaveFunction ? this.translateService.instant('Common.Confirm.Exit') : this.translateService.instant('Common.Confirm.ExitWithoutSave'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.restoreDataEvent();
        console.log(this.value);
        this.isInEditMode = false;
        this.toastService.success(this.translateService.instant('Common.Toast.ExitSuccess'));
      }
    });
  }

  getRowStyle(rowData: any) {
    if (this.rowStyle) {
      return this.rowStyle(rowData);
    }
  }

  initSetupValue() {
    this.checkedRows = [];
    if (this.headerCheckBox) {
      this.headerCheckBox.writeValue(false);
    }
    this.selectedRows = []
    this.selectedRow = undefined;
    this.expandedRows = {};
    this.previousSelectedRow = undefined;
    this.savedDataBean = new DataTableSavedBean();
    
    // 只有在非skipBackup模式下才同步backupValue
    if (!this.skipBackupOnNextInit) {
      this.backupValue = deepCopy(this.value) ?? [];
    }
    // 重置標記
    this.skipBackupOnNextInit = false;
    
    if (this.isInEditMode) {
      this.cancelRowEdit();
    }
  }

  addPaginatorBottomTemplate(): void {
    const paginatorBottom = this.el.nativeElement.querySelector('.p-paginator-bottom');
    if (!paginatorBottom) {
      return;
    }

    const findPDropDownElement = paginatorBottom.querySelector('p-dropdown');
    if (findPDropDownElement) {
      paginatorBottom.removeChild(findPDropDownElement);
    }

    const componentRef = this.viewContainerRef.createComponent(CustomRowsPerPageComponent);
    componentRef.instance.disabled = !this.value.length;
    componentRef.instance.rowsPerPage = this.rows;
    componentRef.instance.rowsPerPageOptions = this.rowsPerPageOptions;
    componentRef.instance.syncRowsPerPage.subscribe((rowsPerPageOptions: number[]) => {
      this.rowsPerPageOptions = rowsPerPageOptions;
      this.table.saveState();
    });
    componentRef.instance.selectRowsPerPage.subscribe((rowsPerPage: number) => {
      /* 計算first與rows相除後的pageIndex，重新設定first應該在的位置 */
      const pageIndex = Math.floor(this.first / this.rows);
      this.first = pageIndex * rowsPerPage;
      /* 同步元件內選擇的每頁筆數 */
      this.rows = rowsPerPage;
      this.setPageSize.emit({ rows: this.rows });
      this.pageEvent({ first: this.first, rows: this.rows });
    });
    this.customRowsPerPageComponent = componentRef.instance;
    this.renderer.appendChild(paginatorBottom, componentRef.location.nativeElement);
  }

  fetchDefaultRowsPerPageOptions(): void {
    this.commonDataService.searchCsmDetailSetting('TableRowsPerPageOption').subscribe((result) => {
      if (!result.length) { return; }
      
      const setting = result.find((x) => x.setupValue === this.tableKey);
      if (!setting || !setting.setupValue2) { return; }

      try {
        const options = JSON.parse(setting.setupValue2) as number[];
        this.rowsPerPageOptions = options.sort((a, b) => a - b);
        this.table.rowsPerPageOptions = this.rowsPerPageOptions;
      } catch (error) {
        console.error(error);
      }
    });
  }

  exportDataEvent(): void {
    this.exportData.emit();
  }

  importDataEvent(): void {
    // 觸發隱藏的文件輸入元素
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // 發出文件資料
      this.importData.emit(file);
      // 清空文件輸入以允許重複選擇相同文件
      event.target.value = '';
    }
  }
}
